import {
  Readable,
  Comment,
  useCommentsQuery,
  useToggleReactionReadableMutation,
  ToggleReactionReadableMutation,
} from 'generated/apolloComponents'
import React, { BaseSyntheticEvent, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useSession } from 'next-auth/react'
import {
  HandThumbUpIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline'
import CommentForm from './CommentForm'
import CommentsTree from './CommentsTree'
import { nested } from '@/helpers/utils'
import Modal from '@/components/molecules/Modal'
import useModal from '@/hooks/useModal'
import AuthContent from '../AuthContent'
import { socialsLogin } from '@/data/auth'

interface IProps {
  readable: Readable
}

const Comments = ({ readable }: IProps) => {
  const [comments, setComments] = useState<Comment[]>([])
  const [nestedComments, setNestedComments] = useState<Comment[]>([])
  const [commentsCount, setCommentsCount] = useState<Number>()
  const [likes, setLikes] = useState(false)
  const [replyIds, setReplyIds] = useState<Number[]>([])
  const { t, lang } = useTranslation('common')
  const { data: session } = useSession()
  const { isOpen, closeModal, openModal } = useModal()

  const setCommentsData = () => {
    setComments(data?.comments.nodes as Comment[])
    setNestedComments(nested(data?.comments.nodes))
    setCommentsCount(data?.comments.totalCount)
  }

  const { loading, data } = useCommentsQuery({
    variables: {
      readableId: readable.id,
    },
    onCompleted: setCommentsData,
  })

  const [toggleReadable] = useToggleReactionReadableMutation({
    variables: {
      input: { readableId: readable.id },
    },
    onCompleted: (data: ToggleReactionReadableMutation) => {
      data?.toggleReactionReadable?.success && setLikes(!likes)
    },
  })

  const updateCommentsList = (data: any) => {
    const newComments = [...comments, data.comment]

    setComments(newComments)
    setReplyIds([])

    setNestedComments(nested(newComments))
  }

  const handleResponseClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    setReplyIds([...replyIds, Number(e.target.dataset.commentid)])
  }

  const handleLike = async (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!session) {
      openModal()
    } else {
      toggleReadable()
    }
  }

  return (
    <>
      {!session && (
        <Modal isOpen={isOpen} closeModal={closeModal}>
          <AuthContent
            data={socialsLogin.map((item) => ({
              ...item,
              title: t(`common:${item.title}`),
            }))}
            title={t('common:login_to_bulldog')}
          />
        </Modal>
      )}
      {loading ? (
        '<p>Loading...</p>'
      ) : (
        <div>
          <div className='flex items-center border-b pb-3 border-gray-100'>
            <div>
              <a href='#' onClick={handleLike} aria-label={t('like')}>
                <HandThumbUpIcon
                  width={32}
                  className={`${likes && 'text-success'}`}
                />
              </a>
              {readable.thumbsup !== 0 && (
                <span className='pl-3'>{readable.thumbsup}</span>
              )}
            </div>
            <div className='ml-8 flex items-center'>
              <ChatBubbleLeftIcon width={32} className='mr-2' />
              {commentsCount !== 0 ? (
                <span className='pl-3'>{commentsCount}</span>
              ) : (
                t('blog:start_discussion')
              )}
            </div>
          </div>
          <div className='mt-6'>
            {nestedComments &&
              nestedComments.map((comment, index) => (
                <CommentsTree
                  key={index}
                  readable={readable}
                  handleResponseClick={handleResponseClick}
                  comment={comment}
                  updateCommentsList={updateCommentsList}
                  replyIds={replyIds}
                />
              ))}
            <div>
              <CommentForm
                readable={readable}
                updateCommentsList={updateCommentsList}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Comments
